import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaCheck, FaExclamationCircle, FaFileAlt, FaEdit } from 'react-icons/fa';

const Step3ClientPendingModal = ({
  isOpen,
  onClose,
  customTable,
  onSubmit,
  isSubmitted,
  isSubmitting = false
}) => {
  const [formData, setFormData] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (customTable) {
      const { customRows, rows, userSubmittedData } = customTable;

      // Initialize form data
      if (userSubmittedData && userSubmittedData.length > 0) {
        setFormData(userSubmittedData);
      } else if (customRows && customRows.length > 0) {
        // New structure: adminValue is in first column, user fills second
        setFormData(customRows.map(() => ['', '']));
      } else if (rows && rows.length > 0) {
        // Old structure fallback
        setFormData(rows.map(row => row.map(() => '')));
      }
    }
  }, [customTable]);

  const handleCellChange = (rowIndex, colIndex, value) => {
    // Only allow editing the second column (Remarks by Client)
    if (colIndex !== 1) return;

    const newFormData = [...formData];
    if (!newFormData[rowIndex]) {
      newFormData[rowIndex] = ['', ''];
    }
    newFormData[rowIndex][colIndex] = value;
    setFormData(newFormData);

    // Clear error for this cell
    const errorKey = `row-${rowIndex}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const { customRows, columns } = customTable;

    formData.forEach((row, rowIndex) => {
      // Check if this row is mandatory
      let isMandatory = false;
      if (customRows && customRows[rowIndex]) {
        isMandatory = customRows[rowIndex].isMandatory;
      } else if (columns && columns[0]?.isMandatory) {
        isMandatory = true;
      }

      if (isMandatory && (!row[1] || row[1].trim() === '')) {
        newErrors[`row-${rowIndex}`] = 'Remarks are required for this item';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    } else {
      alert("Please fill all required fields before submitting.");
    }
  };

  if (!isOpen || !customTable) return null;

  const { customRows, rows, userSubmittedAt, columns } = customTable;
  const tableRows = customRows || rows?.map(r => ({ adminValue: r[0], isMandatory: columns?.[0]?.isMandatory ?? true })) || [];

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[9999] p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-[1.5rem] shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col animate-modalIn border border-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-700 via-blue-600 to-indigo-800 text-white px-8 py-6 flex justify-between items-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
          <div className="relative z-10">
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-3">
              <FaFileAlt className="text-blue-200" />
              Review Draft Tax Return
            </h2>
            <div className="flex items-center gap-3 mt-1.5">
              <p className="text-indigo-100 text-[10px] font-semibold tracking-wide opacity-80">Step 3: Information Confirmation</p>
              {userSubmittedAt && (
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider">
                  Submitted: {new Date(userSubmittedAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className={`text-white/60 hover:text-white hover:bg-white/10 rounded-xl p-2.5 transition-all duration-300 group relative z-10 ${isSubmitting ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            <FaTimes size={20} className={!isSubmitting ? "group-hover:rotate-90 transition-transform duration-300" : ""} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50/20">
          {isSubmitted && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 text-center mb-6 shadow-sm animate-fadeIn">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
                <FaCheck className="text-emerald-600 text-xl" />
              </div>
              <h3 className="text-xl font-black text-emerald-900 mb-2">Submission Received</h3>
              <p className="text-emerald-700 max-w-md mx-auto leading-relaxed text-sm font-semibold">
                Your response has been received. Our team will review your remarks shortly.
              </p>
            </div>
          )}

          {/* Proper Table Design */}
          <div className="overflow-hidden border border-gray-200 rounded-xl shadow-2xl bg-white">
            <table className="w-full border-collapse table-fixed">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-400 tracking-wider w-[45%]">
                    Information Required
                  </th>
                  <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-400 tracking-wider w-[55%]">
                    Remarks by Client
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tableRows.map((config, rowIndex) => (
                  <tr key={rowIndex} className="group hover:bg-gray-50/20 transition-all duration-300">
                    <td className="px-6 py-5 align-top border-r border-gray-50">
                      <div className="flex gap-4">
                        <span className="text-gray-300 font-black text-sm tabular-nums mt-0.5 group-hover:text-indigo-200 transition-colors">
                          {(rowIndex + 1).toString().padStart(2, '0')}
                        </span>
                        <div>
                          <p className="text-gray-700 text-sm font-bold leading-relaxed whitespace-pre-wrap">
                            {config.adminValue}
                          </p>
                          {config.isMandatory && !isSubmitted && (
                            <span className="inline-flex items-center gap-1.5 mt-2.5 text-[9px] font-bold text-rose-500 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-100">
                              Mandatory
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      {isSubmitted ? (
                        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-gray-800 font-bold text-sm leading-relaxed italic shadow-inner min-h-[60px]">
                          {formData[rowIndex]?.[1] || 'No remarks provided'}
                        </div>
                      ) : (
                        <div className="relative">
                          <textarea
                            value={formData[rowIndex]?.[1] || ''}
                            onChange={(e) => handleCellChange(rowIndex, 1, e.target.value)}
                            placeholder="Type remarks..."
                            className={`w-full px-3 py-2 bg-white border-2 rounded-lg text-xs font-bold transition-all duration-300 min-h-[60px] focus:ring-4 ${errors[`row-${rowIndex}`]
                                ? 'border-rose-200 bg-rose-50/20 focus:border-rose-500 focus:ring-rose-500/10'
                                : 'border-gray-50 hover:border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500/10'
                              }`}
                          />
                          {errors[`row-${rowIndex}`] && (
                            <div className="flex items-center gap-1.5 mt-2 text-rose-600 animate-shake">
                              <FaExclamationCircle size={10} />
                              <p className="text-[9px] font-bold tracking-wide">{errors[`row-${rowIndex}`]}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white px-8 py-5 flex justify-end items-center gap-4 border-t border-gray-100 shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.03)] selection:bg-indigo-100">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-8 py-2.5 text-gray-400 font-bold rounded-lg hover:bg-gray-50 hover:text-gray-600 transition-all duration-300 text-[11px] border border-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Close
          </button>
          {!isSubmitted && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`flex items-center gap-2 px-10 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white font-bold rounded-lg hover:from-indigo-700 hover:to-indigo-900 transition-all duration-300 shadow-lg hover:shadow-indigo-200 transform hover:-translate-y-0.5 text-[11px] disabled:opacity-80 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FaCheck size={14} />
                  Submit Response
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Step3ClientPendingModal;
